import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function FAQSection() {
  const {
    lang
  } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    q: '¿Necesito saber de tecnología para trabajar contigo?',
    qEn: 'Do I need to know about technology to work with you?',
    a: 'No. Tú te enfocas en tu negocio — nosotros manejamos todo lo técnico. Al final recibes todo listo y un dashboard para ver tu actividad sin complicaciones.',
    aEn: 'No. You focus on your business — we handle all the technical stuff. At the end you receive everything ready and a dashboard to view your activity without complications.'
  }, {
    q: '¿Puedo contratarte si vivo fuera de Estados Unidos?',
    qEn: 'Can I hire you if I live outside the United States?',
    a: 'Sí. Trabajo con emprendedores en cualquier parte del mundo. El proceso es 100% remoto y en español.',
    aEn: 'Yes. I work with entrepreneurs anywhere in the world. The process is 100% remote and in Spanish.'
  }, {
    q: '¿Cuánto tiempo tarda en estar listo mi negocio digital?',
    qEn: 'How long does it take for my digital business to be ready?',
    a: 'Depende del servicio. El promedio es menos de 2 semanas desde que iniciamos. En la Sesión Diagnóstico definimos el tiempo exacto para tu caso.',
    aEn: 'It depends on the service. The average is less than 2 weeks from when we start. In the Diagnostic Session we define the exact time for your case.'
  }, {
    q: '¿Qué es la Sesión Diagnóstico y por qué es el primer paso?',
    qEn: 'What is the Diagnostic Session and why is it the first step?',
    a: 'Es una sesión de 30 minutos donde analizamos tu situación, identificamos qué necesitas y te doy un plan claro. Los $50 se descuentan de cualquier servicio que contrates.',
    aEn: 'It is a 30-minute session where we analyze your situation, identify what you need, and I give you a clear plan. The $50 is deducted from any service you hire.'
  }, {
    q: '¿Necesito una LLC para tener un negocio digital?',
    qEn: 'Do I need an LLC to have a digital business?',
    a: 'No es obligatorio, pero sí recomendable si quieres operar formalmente en USA, cobrar en dólares con Stripe y proteger tu negocio legalmente. Lo evaluamos juntos en la sesión.',
    aEn: 'It is not mandatory, but it is recommended if you want to operate formally in the USA, charge in dollars with Stripe, and protect your business legally. We evaluate it together in the session.'
  }, {
    q: '¿Por qué contratar Inkova si hoy cualquiera puede usar AI?',
    qEn: 'Why hire Inkova if anyone can use AI today?',
    a: 'Exactamente por eso. La AI está disponible para todos — pero sin saber cómo hablarle, qué pedirle y cómo adaptarla a tu identidad, el resultado es genérico. En Inkova usamos AI como herramienta, no como sustituto del criterio. Lo que nos diferencia es que todo — el mensaje, el diseño, la estrategia — se construye desde quién eres tú, no desde una plantilla.',
    aEn: 'Exactly because of that. AI is available to everyone — but without knowing how to talk to it, what to ask it, and how to adapt it to your identity, the result is generic. At Inkova we use AI as a tool, not as a substitute for judgment. What differentiates us is that everything — the message, the design, the strategy — is built from who you are, not from a template.'
  }, {
    q: '¿Cómo sé cuál de los 3 caminos es el mío?',
    qEn: 'How do I know which of the 3 paths is mine?',
    a: 'Para eso existe la Sesión Diagnóstico — para que no tengas que adivinarlo solo.',
    aEn: "That is what the Diagnostic Session is for — so you don't have to guess alone."
  }];
  return <section id="faq" className="py-24 bg-inkova-cream" data-id="element-108">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-109">
        <div className="text-center mb-14" data-id="element-110">
          <span className="inline-block font-sans text-[11px] font-semibold tracking-[2px] uppercase text-inkova-primary bg-inkova-primary/10 px-3.5 py-1.5 rounded-full mb-4" data-id="element-111">
            {lang === 'es' ? 'PREGUNTAS FRECUENTES' : 'FREQUENTLY ASKED QUESTIONS'}
          </span>
          <h2 className="text-[28px] sm:text-[3vw] lg:text-[42px] font-serif text-inkova-dark mb-3" data-id="element-112">
            {lang === 'es' ? 'Preguntas frecuentes' : 'Frequently asked questions'}
          </h2>
          <p className="text-base text-[#6b6478]" data-id="element-113">
            {lang === 'es' ? 'Si tienes alguna que no esté aquí — escríbeme directamente.' : 'If you have any that are not here — write to me directly.'}
          </p>
        </div>

        <div className="max-w-[800px] mx-auto flex flex-col gap-3" data-id="element-114">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.05
        }} className="bg-white rounded-xl border border-inkova-primary/10 overflow-hidden" data-id="element-115">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className={`w-full flex justify-between items-center p-5 sm:px-7 text-left transition-colors duration-200 ${openIndex === index ? 'text-inkova-primary bg-inkova-primary/5' : 'text-inkova-dark hover:bg-inkova-primary/5'}`} data-id="element-116">
                <span className="text-[15px] font-semibold pr-4" data-id="element-117">
                  {lang === 'es' ? faq.q : faq.qEn}
                </span>
                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-300 ${openIndex === index ? 'border-inkova-primary bg-inkova-primary text-white' : 'border-inkova-primary text-inkova-primary'}`} data-id="element-118">
                  {openIndex === index ? <Minus className="w-4 h-4" data-id="element-119" /> : <Plus className="w-4 h-4" data-id="element-120" />}
                </div>
              </button>

              <AnimatePresence data-id="element-121">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} data-id="element-122">
                    <div className="px-5 sm:px-7 pb-6 pt-2" data-id="element-123">
                      <p className="text-sm text-[#6b6478] leading-[1.75]" data-id="element-124">
                        {lang === 'es' ? faq.a : faq.aEn}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}